import './RoutePlanner.scss'

import { HDSButton, HDSProgressIndicator } from '@here/hds-react-components'
import classNames from 'classnames'
import PdsMarker from 'components/Map/components/PdsMarker'
import { addNotification } from 'components/Notifications'
import { OperationType } from 'components/RouteInputs/RouteInputs'
import ShareLinkCard from 'components/ShareLinkCard'
import ViewportOverlay from 'components/ViewportOverlay'
import { Coordinates } from 'data/model'
import { RoutePlannerStatus, RouteWaypoint, StateRoute } from 'data/model/routePlanner'
import {
  fromSearchResultEntityItemToSearchStateListItem,
  fromSerializedRecentItemToSearchStateListItem,
  getSearchDistanceLabel,
  SearchStateListItem,
} from 'data/model/search'
import { RootState } from 'data/reducer'
import { setPdcItem } from 'data/reducer/pdc'
import {
  fetchRoutes,
  setRoutes,
  setRoutesAutoSuggestLocations,
  setRoutesRecentLocations,
  setRouteWaypoints,
  setSelectedRoute,
  setSelectedTransportMode,
} from 'data/reducer/routePlanner'
import {
  clearRecentItemList,
  fetchLocationAddress,
  setRecentItem,
  setSearchItemList,
} from 'data/reducer/search'
import { useAppDispatch, useAppSelector } from 'data/store/hooks'
import { IzumiMessageKey } from 'izumi/IzumiMessageKey'
import { mapConfig } from 'lib/config'
import {
  createDeepLink,
  createDirectionsPath,
  getDirectionsData,
  MapType,
  TransportMode as PathTransportMode,
  Waypoint,
  WaypointType,
} from 'lib/directions'
import { PageType, useNavigation } from 'lib/hooks'
import { i18n } from 'lib/i18n'
import { BaseLayerNames } from 'map/layers'
import { createPlatform } from 'map/platform'
import {
  createAutosuggestRequestParams,
  isQueryItem,
  SearchResultEntityItem,
  SearchService,
} from 'map/services'
import React, { MouseEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useMatch, useSearchParams } from 'react-router-dom'
import {
  areWaypointsSame,
  getRouteDestination,
  getRouteStops,
  isSourceDestinationSet,
  sliceEmptyWaypoints,
} from 'utils/routeplanner'
import { MapSearchParam, parseMapSearchParam } from 'utils/router'
import {
  getRecentList,
  RecentItemType,
  removeRecentItem,
  SerializedRecentItem,
} from 'utils/storage'

import List, { DividerProps } from '../App/components/List'
import { ScrollableContainer } from '../App/components/ScrollableContainer'
import { EmptyResults } from '../EmptyResults'
import PdcMarker from '../Map/PdcMarker'
import MapRoutesLayer from '../MapRoutesLayer'
import Pdc, { PdcEvent } from '../Pdc'
import Rdc from '../Rdc/Rdc'
import RecentList from '../RecentList'
import RouteInputs from '../RouteInputs'
import { RouteListItem } from '../RouteListItem'
import { Item, SearchResultList } from '../SearchResultList'
import RoutePreferences from './RoutePreferences'
import {
  TransportModesPanel,
  TransportModesPanelMode,
  TransportPanelOption,
} from './TransportModesPanel'

interface RoutePlannerProps {
  map: H.Map
  handleWaypointMarkerCreation: (marker: H.map.Marker) => void
}

const searchService = createPlatform().getSearchService() as unknown as SearchService

export const RoutePlanner = ({
  map,
  handleWaypointMarkerCreation,
}: RoutePlannerProps): JSX.Element => {
  const dispatch = useAppDispatch()
  const { goTo, goBack } = useNavigation()
  const mapSearchParamStr = useSearchParams()[0].get('map')
  const mapSearchParam = useMemo(
    () => (mapSearchParamStr !== null ? parseMapSearchParam(mapSearchParamStr) : undefined),
    [mapSearchParamStr]
  )
  const mapSearchParamRef = useRef<MapSearchParam | undefined>(mapSearchParam)

  const countryCode = useAppSelector((state) => state.user.countryCode)
  const [guidanceOptChecked, setGuidanceOptChecked] = useState<boolean>(
    localStorage.getItem('GUIDANCE_OPT_CHECKED') === 'true'
  )
  const unit = useAppSelector((state) => state.app.unit)
  const userPosition = useAppSelector((state: RootState) => state.user.currentPosition)
  const appLanguage = useAppSelector((state: RootState) => state.app.language)
  const isRouteFound = useAppSelector(({ routePlanner }) => routePlanner.routes.length > 0)
  const routePlannerState = useAppSelector(({ routePlanner }) => routePlanner)
  const hasSelectedRoute = useAppSelector(({ routePlanner }) => Boolean(routePlanner.selectedRoute))
  const pdcItem = useAppSelector(({ pdc }) => pdc.pdcItem)
  const [highlightedRouteId, setHighlightedRouteId] = useState<string>()
  const [showShareCard, setShowShareCard] = useState<boolean>(false)
  const [deeplinkUrl, setDeepLinkUrl] = useState<string>('')
  const selectedTransportMode = useAppSelector(
    ({ routePlanner }) => routePlanner.selectedTransportMode
  )
  const isRouteFetching = useAppSelector(
    ({ routePlanner }) => routePlanner.status === RoutePlannerStatus.ROUTES_PENDING
  )
  const hasEmptySuggestions = useAppSelector(
    ({ routePlanner }) =>
      routePlanner.routesRecentLocations.length === 0 &&
      routePlanner.routesAutoSuggestLocations.length === 0
  )
  const hasWaypoints = useAppSelector(({ routePlanner }) =>
    isSourceDestinationSet(routePlanner.routeWaypoints)
  )

  const [focusIndex, setFocusIndex] = useState<number>(-1)
  const [contextPdcLabel, setContextPdcLabel] = useState<string>('')
  const isAutoFocusRef = useRef<boolean>(true)
  const isAutoSuggestClickedRef = useRef<boolean>(false)
  const isStopAddedRef = useRef<boolean>(false)
  const isRouteListShownOnMapRef = useRef<boolean>(true)
  const requestRef = useRef<H.util.ICancelable>({ cancel: () => undefined })
  const waypointsRef = useRef<RouteWaypoint[]>([])
  const destinationRef = useRef<RouteWaypoint>({} as RouteWaypoint)

  const routeWaypointsPath = useMatch('/directions/*')
  const countryAndUnit = useMemo(() => {
    return { countryCode, unit }
  }, [countryCode, unit])

  const transportModes: TransportPanelOption[] = mapConfig.transportModes
    .filter(
      (transportMode) =>
        (transportMode !== 'rail' && transportMode !== 'bicycle') ||
        transportMode === 'rail' ||
        transportMode === 'bicycle'
    )
    .map((mode) => {
      let transportMode = TransportModesPanelMode.Car

      switch (mode) {
        case 'car':
          transportMode = TransportModesPanelMode.Car
          break
        case 'pedestrian':
          transportMode = TransportModesPanelMode.Walk
          break
        case 'bicycle':
          transportMode = TransportModesPanelMode.Bike
          break
        case 'rail':
          transportMode = TransportModesPanelMode.PublicTransport
      }

      return {
        mode: transportMode,
        title: i18n(`transport-mode.${mode}` as IzumiMessageKey),
      }
    })

  const dividerOptions: DividerProps = { position: { left: '0.875rem' } }
  const currentLocationLabel = i18n('route-planner.current-location-input-placeholder')
  const showRoutes =
    isRouteFound && hasEmptySuggestions && !hasSelectedRoute && isRouteListShownOnMapRef.current

  // TODO this is a callback to dispatch the selected route from the route component
  const handleRouteListItemClick = useCallback(
    (route: StateRoute): void => {
      dispatch(setSelectedRoute(route))
    },
    [dispatch]
  )

  const handleRouteListItemHover = useCallback((id: string) => {
    setHighlightedRouteId(id)
  }, [])

  const handleLinkShare = useCallback(() => {
    dispatch(
      addNotification({
        content: i18n('notifications.clipboard-success'),
        duration: 5,
        inline: true,
        variant: 'confirmation',
      })
    )
  }, [dispatch])

  const handleGuidanceChange = useCallback((checked: boolean) => {
    localStorage.setItem('GUIDANCE_OPT_CHECKED', checked.toString())
    setGuidanceOptChecked(checked)
  }, [])

  const shouldFetchPTRoute = useCallback((waypoints: RouteWaypoint[]): boolean => {
    return Boolean(
      waypoints.length > 1 &&
        waypoints[0].lat &&
        waypoints[0].lng &&
        waypoints[waypoints.length - 1].lat &&
        waypoints[waypoints.length - 1].lng
    )
  }, [])

  // TODO function to check if there is the current location is chosen and is set in Waypoint list
  const isYourLocationPresentInWayPoints = useCallback(
    (routeWayPoints: RouteWaypoint[]): boolean => {
      return (
        routeWayPoints.filter(
          (wayPoint) => wayPoint !== undefined && wayPoint.place === currentLocationLabel
        ).length > 0
      )
    },
    [currentLocationLabel]
  )

  const getAutoSuggestItems = useCallback(
    (value: string) => {
      // TODO: Once Map becomes a reducer we could move most of this logic to an epic
      const center = map?.getCenter()
      requestRef.current = searchService.autosuggest(
        createAutosuggestRequestParams(
          value,
          userPosition.coordinates !== null
            ? ({
                lat: userPosition.coordinates?.lat,
                lng: userPosition.coordinates?.lng,
              } as H.geo.Point)
            : center,
          appLanguage
        ),
        ({ items }) => {
          dispatch(setRoutesAutoSuggestLocations(items.filter((item) => !isQueryItem(item))))

          if (routePlannerState.routesRecentLocations.length > 0) {
            dispatch(setRoutesRecentLocations([]))
          }
        },
        (error) => console.warn('"/autosuggest" request error', error)
      )
    },
    [
      map,
      userPosition.coordinates,
      appLanguage,
      routePlannerState.routesRecentLocations.length,
      dispatch,
    ]
  )

  const setRecentList = useCallback(
    (showYourLocation = true) => {
      const recentList = getRecentList()
        .filter(
          (item) =>
            item.lat !== null &&
            item.lat !== undefined &&
            item.lon !== null &&
            item.lon !== undefined
        )
        .slice(0, Number(mapConfig.services.search.recent.limit))
      recentList.forEach((item) => {
        item.distanceLabel = getSearchDistanceLabel(countryAndUnit, userPosition.coordinates, {
          lat: item.lat,
          lng: item.lon,
        })
      })

      if (userPosition.coordinates !== null && showYourLocation) {
        const { lat, lng: lon } = userPosition.coordinates
        const currentLocationItem = {
          lat,
          lon,
          placeId: 'current-location',
          timestamp: new Date().toISOString(),
          title: currentLocationLabel,
          type: RecentItemType.currentLocation,
        } as SerializedRecentItem
        recentList.unshift(currentLocationItem)
        recentList.slice(0, 5)
      }
      if (recentList?.length > 0) {
        dispatch(setRoutesRecentLocations(recentList))
      }
    },
    [userPosition.coordinates, currentLocationLabel, countryAndUnit, dispatch]
  )

  const removeEmptyWaypoints = useCallback((routeWaypoints: RouteWaypoint[]) => {
    const source = routeWaypoints[0]
    const emptyPoint = {} as RouteWaypoint
    const filteredRoutes = routeWaypoints.filter(
      (waypoint) => waypoint !== undefined && waypoint.lat && waypoint.lng
    )
    if (filteredRoutes.length === 0) {
      return [{ ...emptyPoint }, emptyPoint]
    }
    if (filteredRoutes.length === 1) {
      if (source === undefined || !source.lat || !source.lng) {
        return [emptyPoint, filteredRoutes[0]]
      }
      return [filteredRoutes[0], emptyPoint]
    }
    return filteredRoutes
  }, [])

  const updateDirectionsUrlPath = useCallback(
    (routeWaypoints: RouteWaypoint[] | undefined[]): void => {
      const waypoints: Waypoint[] = routeWaypoints.map((waypoint) => {
        return waypoint !== undefined && waypoint.lat !== undefined
          ? {
              lat: waypoint.lat,
              lng: waypoint.lng,
              name: encodeURIComponent(waypoint.place),
              type: WaypointType.full,
            }
          : { type: WaypointType.empty }
      })

      const path = createDirectionsPath(
        waypoints,
        toTransportMode<TransportModesPanelMode, PathTransportMode>(selectedTransportMode)
      )

      goTo({
        type: PageType.ROUTE_PLANNER,
        url: `/directions/${path}`,
      })
    },
    [goTo, selectedTransportMode]
  )

  const updateWaypoints = useCallback(
    (list: RouteWaypoint[], item?: RouteWaypoint, index?: number): void => {
      if (item !== undefined && index !== undefined) {
        list[index] = item
      }

      dispatch(setRouteWaypoints(list))
    },
    [dispatch]
  )

  const resetAutosuggestList = useCallback(() => {
    dispatch(setRoutesAutoSuggestLocations([]))
    dispatch(setRoutesRecentLocations([]))
  }, [dispatch])

  const updateFocusIndex = useCallback(
    (routeWaypoints: RouteWaypoint[]) => {
      const emptyWaypoint = routeWaypoints.find((w) => !w.lat && !w.lng)
      if (emptyWaypoint && !isStopAddedRef.current) {
        isRouteListShownOnMapRef.current = false
      } else {
        isRouteListShownOnMapRef.current = true
      }
      const index = !emptyWaypoint ? -1 : routeWaypoints.indexOf(emptyWaypoint)
      setFocusIndex(index)
      if (index > -1) {
        setRecentList(!isYourLocationPresentInWayPoints(routeWaypoints))
      } else {
        resetAutosuggestList()
      }
    },
    [isYourLocationPresentInWayPoints, resetAutosuggestList, setRecentList]
  )

  const submitRouteInputs = useCallback(
    (waypoints: RouteWaypoint[], item?: SearchStateListItem) => {
      isRouteListShownOnMapRef.current = true
      isStopAddedRef.current = false
      const routeWaypoints = [...waypoints]

      if (item?.position?.lat !== undefined && item.position.lng !== undefined && focusIndex > -1) {
        routeWaypoints[focusIndex] = {
          lat: item.position.lat,
          lng: item.position.lng,
          place: item.title,
        }
        if (item.id !== 'current-location') {
          dispatch(setRecentItem(item))
        }
      }

      const filteredWaypoints = removeEmptyWaypoints(routeWaypoints)
      updateDirectionsUrlPath(filteredWaypoints)
      updateFocusIndex(filteredWaypoints)
      // In case of submittion then input was not getting populated with title of the item
      // as state.routeWaypoints was not changed hence needed to update value of route inputs
    },
    [focusIndex, removeEmptyWaypoints, updateDirectionsUrlPath, updateFocusIndex, dispatch]
  )

  const handleRouteInputFocus = useCallback(
    (index: number, value: string) => {
      setFocusIndex(index)
      isAutoFocusRef.current = false
      isAutoSuggestClickedRef.current = false
      if (value) {
        resetAutosuggestList()
      } else {
        setRecentList(!isYourLocationPresentInWayPoints(routePlannerState.routeWaypoints))
      }
    },
    [
      isYourLocationPresentInWayPoints,
      resetAutosuggestList,
      routePlannerState.routeWaypoints,
      setRecentList,
    ]
  )

  const handleRouteInputsBlur = useCallback((): void => {
    setFocusIndex(-1)
    resetAutosuggestList()
  }, [resetAutosuggestList])

  const handleRouteInputEnterKeyDown = useCallback(() => {
    if (
      routePlannerState.routesAutoSuggestLocations.length > 0 ||
      routePlannerState.routesRecentLocations.length > 0
    ) {
      if (routePlannerState.routesAutoSuggestLocations.length > 0) {
        const item = routePlannerState.routesAutoSuggestLocations[0] as SearchResultEntityItem
        submitRouteInputs(
          routePlannerState.routeWaypoints,
          fromSearchResultEntityItemToSearchStateListItem(
            item,
            countryAndUnit,
            userPosition.coordinates
          )
        )
      } else if (routePlannerState.routesRecentLocations.length > 0) {
        const item = routePlannerState.routesRecentLocations[0]
        submitRouteInputs(
          routePlannerState.routeWaypoints,
          fromSerializedRecentItemToSearchStateListItem(
            item,
            countryAndUnit,
            userPosition.coordinates
          )
        )
      }
    }

    resetAutosuggestList()
  }, [
    routePlannerState.routesAutoSuggestLocations,
    routePlannerState.routesRecentLocations,
    routePlannerState.routeWaypoints,
    resetAutosuggestList,
    submitRouteInputs,
    countryAndUnit,
    userPosition.coordinates,
  ])

  const handleRouteWaypointChange = useCallback(
    (waypoints: RouteWaypoint[], operation?: OperationType) => {
      isStopAddedRef.current = false
      updateFocusIndex(waypoints)
      if (operation === OperationType.add) {
        isStopAddedRef.current = true
        updateDirectionsUrlPath(waypoints)
      } else if (operation === OperationType.reorder) {
        updateDirectionsUrlPath(waypoints)
      } else if (operation === OperationType.remove) {
        submitRouteInputs(waypoints)
      }
    },
    [submitRouteInputs, updateDirectionsUrlPath, updateFocusIndex]
  )

  const handleTransportModeClick = useCallback(
    (transportMode: TransportModesPanelMode) => {
      if (
        routeWaypointsPath !== null &&
        routeWaypointsPath.params !== null &&
        routeWaypointsPath.params['*'] !== undefined
      ) {
        const pathname = routeWaypointsPath.pathname.replace(
          `${routeWaypointsPath.pathnameBase}`,
          ''
        )
        const directionsData = getDirectionsData(pathname)
        const waypoints = getWaypointsWithUserPosition(
          directionsData.waypoints,
          userPosition.coordinates,
          currentLocationLabel
        )

        const path = createDirectionsPath(
          waypoints,
          toTransportMode<TransportModesPanelMode, PathTransportMode>(transportMode)
        )

        goTo({
          type: PageType.ROUTE_PLANNER,
          url: `/directions/${path}`,
        })
      }
    },
    [routeWaypointsPath, userPosition.coordinates, currentLocationLabel, goTo]
  )

  const handleOnAutoSuggestClick = useCallback(
    (item: Item) => {
      isAutoSuggestClickedRef.current = true
      const selectedAutoSuggestItem = item as unknown as SearchResultEntityItem

      submitRouteInputs(
        routePlannerState.routeWaypoints,
        fromSearchResultEntityItemToSearchStateListItem(
          selectedAutoSuggestItem,
          countryAndUnit,
          userPosition.coordinates
        )
      )
    },
    [routePlannerState, submitRouteInputs, countryAndUnit, userPosition.coordinates]
  )

  const handleRouteRecentItemClick = useCallback(
    (item: SerializedRecentItem) => {
      submitRouteInputs(
        routePlannerState.routeWaypoints,
        fromSerializedRecentItemToSearchStateListItem(
          item,
          countryAndUnit,
          userPosition.coordinates
        )
      )
    },
    [routePlannerState, submitRouteInputs, countryAndUnit, userPosition.coordinates]
  )

  const resetRouteWayPoints = useCallback((): RouteWaypoint[] => {
    const routeWayPoints: RouteWaypoint[] = [...routePlannerState.routeWaypoints]
    routeWayPoints[focusIndex] = {} as RouteWaypoint
    updateDirectionsUrlPath(routeWayPoints)
    return routeWayPoints
  }, [routePlannerState.routeWaypoints, focusIndex, updateDirectionsUrlPath])

  const resetAutoSuggestList = useCallback(() => {
    dispatch(setRoutesAutoSuggestLocations([]))
  }, [dispatch])

  const handleRouteInputChange = useCallback(
    (value: string): void => {
      isRouteListShownOnMapRef.current = false
      if (value?.trim()) {
        getAutoSuggestItems(value)
      } else {
        resetAutoSuggestList()
        setRecentList(!isYourLocationPresentInWayPoints(resetRouteWayPoints()))
      }
    },
    [
      getAutoSuggestItems,
      isYourLocationPresentInWayPoints,
      resetAutoSuggestList,
      resetRouteWayPoints,
      setRecentList,
    ]
  )

  const shouldFetchRoute = useCallback(
    (waypoints: RouteWaypoint[], transportMode: TransportModesPanelMode): boolean => {
      // if the transport mode has changed you should fetch again
      if (transportMode !== selectedTransportMode) {
        return true
      }

      // If Public Transit - only check first and last waypoint
      if (transportMode === TransportModesPanelMode.PublicTransport) {
        return shouldFetchPTRoute(waypoints)
      }
      // If Not Public Transit - remove all empty waypoints and check if we have a valid route
      // Route should only be fetched if there is actual change in waypoints list
      const slicedWaypoints = sliceEmptyWaypoints(waypoints)
      if (
        waypointsRef.current.length !== slicedWaypoints.length ||
        slicedWaypoints.find(
          (waypoint, index) => !areWaypointsSame(waypoint, waypointsRef.current[index])
        )
      ) {
        waypointsRef.current = slicedWaypoints
        return true
      }

      waypointsRef.current = slicedWaypoints
      return false
    },
    [selectedTransportMode, shouldFetchPTRoute]
  )

  const getMapType = useCallback((): MapType => {
    switch (mapSearchParamRef.current?.baseLayer) {
      case BaseLayerNames.SATELLITE:
      case BaseLayerNames.SATELLITE_TRAFFIC:
        return MapType.satellite
      case BaseLayerNames.TRAFFIC:
        return MapType.traffic
      case BaseLayerNames.OMV:
      case BaseLayerNames.NORMAL:
      default:
        return MapType.normal
    }
  }, [])

  // This effect will fetch the routes when the route path changes for the router page.
  useEffect(() => {
    if (
      routeWaypointsPath === null ||
      routeWaypointsPath.params === null ||
      routeWaypointsPath.params['*'] === undefined
    ) {
      return
    }

    const pathname = routeWaypointsPath.pathname.replace(`${routeWaypointsPath.pathnameBase}`, '')
    const directionsData = getDirectionsData(pathname)
    const waypoints = getWaypointsWithUserPosition(
      directionsData.waypoints,
      userPosition.coordinates,
      currentLocationLabel
    )
    const transportMode = directionsData.mode

    const routeWaypoints: RouteWaypoint[] = waypoints.map((waypoint) => {
      return waypoint.type !== WaypointType.empty
        ? {
            lat: waypoint.lat as number,
            lng: waypoint.lng as number,
            place: waypoint.name as string,
          }
        : ({} as RouteWaypoint)
    })

    updateWaypoints(routeWaypoints)
    const mode = toTransportMode<PathTransportMode, TransportModesPanelMode>(directionsData.mode)
    dispatch(setSelectedTransportMode(mode))
    isAutoFocusRef.current && updateFocusIndex(routeWaypoints)

    const insuficientWaypointData =
      !isSourceDestinationSet(routeWaypoints) ||
      (transportMode === PathTransportMode.publicTransport && !shouldFetchPTRoute(routeWaypoints))

    if (insuficientWaypointData) {
      dispatch(setRoutes([]))
      waypointsRef.current = routeWaypoints
    } else if (shouldFetchRoute(routeWaypoints, mode)) {
      dispatch(fetchRoutes(routeWaypoints, mode))
    }

    const deeplinkWaypoints =
      transportMode === PathTransportMode.publicTransport
        ? [waypoints[0], waypoints[waypoints.length - 1]]
        : waypoints

    setDeepLinkUrl(
      createDeepLink(
        deeplinkWaypoints,
        transportMode,
        guidanceOptChecked,
        undefined,
        mapSearchParamRef.current?.zoom,
        getMapType()
      )
    )

    const destination =
      mode === TransportModesPanelMode.Motorcycle
        ? routeWaypoints[routeWaypoints.length - 1]
        : getRouteDestination(routeWaypoints)

    if (destination && destination.lat && destination.lng) {
      if (
        destination.lat &&
        destination.lng &&
        !areWaypointsSame(destination, destinationRef.current)
      ) {
        dispatch(
          fetchLocationAddress({
            lat: destination.lat,
            lng: destination.lng,
          })
        )
      }

      // update this flag here to ensure that it's value is going to update when the url changes.
      const emptyWaypoint = routeWaypoints.find((w) => !w.lat && !w.lng)
      if (emptyWaypoint) {
        isRouteListShownOnMapRef.current = false
      } else {
        isRouteListShownOnMapRef.current = true
      }
    }
    destinationRef.current = destination ?? ({} as RouteWaypoint)
  }, [
    shouldFetchRoute,
    routeWaypointsPath,
    dispatch,
    selectedTransportMode,
    userPosition.coordinates,
    shouldFetchPTRoute,
    currentLocationLabel,
    handleRouteInputsBlur,
    updateWaypoints,
    updateFocusIndex,
    getMapType,
    guidanceOptChecked,
  ])

  const handleOnRoutingCloseButtonClick = (): void => {
    goBack()
    dispatch(setSearchItemList([]))
    dispatch(setRoutes([]))
    dispatch(setRoutesAutoSuggestLocations([]))
    dispatch(setRouteWaypoints([]))
  }

  const handleOnContextualAction = useCallback(
    (pdc: PdcEvent) => {
      const item: RouteWaypoint = { lat: pdc.lat, lng: pdc.lng, place: pdc.title }
      const routeWaypoints = [...waypointsRef.current]

      if (waypointsRef.current[0].place === undefined) {
        routeWaypoints[0] = item
      } else if (waypointsRef.current[waypointsRef.current.length - 1].place === undefined) {
        routeWaypoints[routeWaypoints.length - 1] = item
      } else {
        routeWaypoints.push(item)
      }

      updateDirectionsUrlPath(routeWaypoints)
      dispatch(setPdcItem(undefined))
    },
    [dispatch, updateDirectionsUrlPath]
  )

  const handlePdcContextualLabel = useCallback(() => {
    let label: IzumiMessageKey = 'contextual.pdc.add-stop'

    if (waypointsRef.current[0].place === undefined) {
      label = 'contextual.pdc.set-as-starting-point'
    } else if (waypointsRef.current[waypointsRef.current.length - 1].place === undefined) {
      label = 'contextual.pdc.set-as-destination'
    }

    setContextPdcLabel(i18n(label))
  }, [])

  const handleRoutesLayerHover = useCallback(
    (id?: string) => {
      !routePlannerState.selectedRoute && setHighlightedRouteId(id)
    },
    [routePlannerState.selectedRoute]
  )

  useEffect(() => {
    if (pdcItem) {
      handlePdcContextualLabel()
    }
  }, [pdcItem, handlePdcContextualLabel])

  const handleCloseContextualPdc = useCallback(() => {
    dispatch(setPdcItem(undefined))
  }, [dispatch])

  const clearRecentList = useCallback(
    (showYourLocation: boolean) => {
      const recentList = []
      if (userPosition.coordinates !== null && showYourLocation) {
        const { lat, lng: lon } = userPosition.coordinates
        const currentLocationItem = {
          lat,
          lon,
          placeId: 'current-location',
          timestamp: new Date().toISOString(),
          title: currentLocationLabel,
          type: RecentItemType.currentLocation,
        } as SerializedRecentItem
        recentList.push(currentLocationItem)
      }
      dispatch(setRoutesRecentLocations(recentList))
    },
    [currentLocationLabel, dispatch, userPosition.coordinates]
  )

  const handleRecentClearClick = (e: MouseEvent): void => {
    e.stopPropagation()
    e.preventDefault()
    dispatch(clearRecentItemList('route'))
    clearRecentList(!isYourLocationPresentInWayPoints(routePlannerState.routeWaypoints))
  }

  const handleDeleteRecentItem = useCallback(
    (e: MouseEvent, id: string): void => {
      e.stopPropagation()
      e.preventDefault()
      removeRecentItem(id)
      if (routePlannerState.routesRecentLocations.length === 1) {
        clearRecentList(!isYourLocationPresentInWayPoints(routePlannerState.routeWaypoints))
      } else {
        setRecentList(!isYourLocationPresentInWayPoints(routePlannerState.routeWaypoints))
      }
    },
    [
      clearRecentList,
      isYourLocationPresentInWayPoints,
      routePlannerState.routeWaypoints,
      routePlannerState.routesRecentLocations.length,
      setRecentList,
    ]
  )

  const handleRoutePreferencesChange = useCallback(
    (routeWaypoints: RouteWaypoint[]) => {
      dispatch(fetchRoutes(routeWaypoints, selectedTransportMode))
    },
    [dispatch, selectedTransportMode]
  )

  const showEmptyResult =
    (isRouteListShownOnMapRef.current === null ||
      !isRouteListShownOnMapRef.current ||
      !isRouteFound) &&
    hasEmptySuggestions &&
    hasWaypoints

  const isPTmode = selectedTransportMode === TransportModesPanelMode.PublicTransport

  return (
    <>
      {pdcItem?.id !== undefined ? (
        <>
          <Pdc
            id={pdcItem.id}
            onActionClick={handleOnContextualAction}
            actionLabel={contextPdcLabel}
            onCloseButtonClick={handleCloseContextualPdc}
            data={{
              deepLinkMapType: getMapType(),
              deepLinkZoomLevel: mapSearchParam?.zoom,
            }}
          />
          {pdcItem.position && (
            <PdcMarker
              id={pdcItem.primaryCategory?.id}
              onMarkerCreate={handleWaypointMarkerCreation}
              lat={pdcItem.position.lat}
              lng={pdcItem.position.lng}
            />
          )}
        </>
      ) : (
        <div className="Map__panel Map__panel--top Map__panel--left Map__card">
          {routePlannerState.selectedRoute ? (
            <Rdc
              destinationWaypoint={destinationRef.current ?? ({} as RouteWaypoint)}
              route={routePlannerState.selectedRoute}
            />
          ) : (
            <>
              <RouteInputs
                routeWaypoints={routePlannerState.routeWaypoints}
                focusIndex={focusIndex}
                isMyLocationShared={userPosition.coordinates !== null}
                onCloseButtonClick={handleOnRoutingCloseButtonClick}
                onEnterKeyDown={handleRouteInputEnterKeyDown}
                onRouteInputChange={handleRouteInputChange}
                onFocus={handleRouteInputFocus}
                onRouteWaypointChange={handleRouteWaypointChange}
                onRouteInputsBlur={handleRouteInputsBlur}
                disabledStops={isPTmode}
                showAddStopBtn={
                  hasEmptySuggestions &&
                  routePlannerState.routeWaypoints.every((w) => w.lat && w.lng)
                }
              />
              {routePlannerState.routesRecentLocations.length > 0 &&
                routePlannerState.routesAutoSuggestLocations.length === 0 && (
                  <RecentList
                    itemList={routePlannerState.routesRecentLocations}
                    mapSearchParamStr={null}
                    OnItemMouseDown={handleRouteRecentItemClick}
                    onItemClick={() => null}
                    onClearAllClick={handleRecentClearClick}
                    onDeleteItem={handleDeleteRecentItem}
                  />
                )}
              {routePlannerState.routesAutoSuggestLocations.length > 0 && (
                <SearchResultList
                  isMyLocationShared={userPosition.coordinates !== null}
                  mapSearchParamStr={null}
                  resultItemList={routePlannerState.routesAutoSuggestLocations}
                  resultsKind="autosuggest"
                  onItemClick={() => null}
                  onItemMouseDown={handleOnAutoSuggestClick}
                />
              )}
              {hasEmptySuggestions && hasWaypoints && (
                <TransportModesPanel
                  isRouteFetching={isRouteFetching}
                  selectedOption={selectedTransportMode}
                  options={transportModes}
                  onClick={handleTransportModeClick}
                />
              )}

              <ScrollableContainer className="RouteListItem__ListItems-container">
                <>
                  {(showEmptyResult || showRoutes) && (
                    <RoutePreferences
                      key={selectedTransportMode}
                      onRoutePreferencesChange={handleRoutePreferencesChange}
                      selectedTransportMode={selectedTransportMode}
                    />
                  )}
                  {showRoutes && (
                    <>
                      <List divider={dividerOptions}>
                        {routePlannerState.routes.map((route) => (
                          <RouteListItem
                            key={route.id}
                            hightlight={highlightedRouteId === route.id}
                            route={route}
                            transportMode={selectedTransportMode}
                            onHover={handleRouteListItemHover}
                            onClick={() => handleRouteListItemClick(route)}
                          />
                        ))}
                      </List>
                      <HDSButton
                        className="RoutePlanner__share-route-button"
                        iconCategory="discovery-sharing"
                        icon="share-iOS"
                        variant="link"
                        size="large"
                        onClick={() => setShowShareCard(true)}
                      >
                        {i18n('share.share-route-button')}
                      </HDSButton>
                    </>
                  )}
                  {showEmptyResult && (
                    <EmptyResults
                      className="Map__empty-results"
                      title={i18n('no-results-card.canot-find-a-route')}
                      message={i18n('no-results-card.message-label-routes-search')}
                    />
                  )}
                  {isRouteFetching && (
                    <div className="RouteListItem__ListItems--loader">
                      <HDSProgressIndicator
                        color="accent"
                        size="small"
                        variant="indeterminate"
                        indicatorType="circular"
                      />
                    </div>
                  )}
                </>
              </ScrollableContainer>
            </>
          )}
        </div>
      )}

      {destinationRef.current !== null &&
        destinationRef.current.lng &&
        destinationRef.current.lat && (
          <>
            {isStopAddedRef.current ? (
              <PdsMarker
                id={'destination-marker'}
                lat={destinationRef.current.lat}
                lng={destinationRef.current.lng}
                zIndex={10}
                onMarkerCreate={handleWaypointMarkerCreation}
              />
            ) : (
              <PdcMarker
                id="destination-marker"
                lat={destinationRef.current.lat}
                lng={destinationRef.current.lng}
                onMarkerCreate={handleWaypointMarkerCreation}
              />
            )}
          </>
        )}
      {Boolean(isRouteListShownOnMapRef.current) && routePlannerState.routes.length > 0 && (
        <>
          <MapRoutesLayer
            map={map}
            highlightedId={highlightedRouteId}
            onHover={handleRoutesLayerHover}
          />
          {!isPTmode &&
            getRouteStops(routePlannerState.routeWaypoints).map((point, index) => (
              <PdsMarker
                key={index}
                isHovered={true}
                id={`stop-marker-${index}`}
                zIndex={8}
                lat={point.lat}
                lng={point.lng}
                onMarkerCreate={handleWaypointMarkerCreation}
              />
            ))}
          {showShareCard && (
            <ViewportOverlay onClick={() => setShowShareCard(false)}>
              <ShareLinkCard
                className={classNames('RoutePlanner__share-route-card', {
                  'RoutePlanner__share-route-card-PT': isPTmode,
                })}
                url={deeplinkUrl}
                isGuidanceEnabled={!isPTmode}
                isGuidanceVisible={!isPTmode}
                isGuidanceChecked={guidanceOptChecked}
                onGuidanceToggle={handleGuidanceChange}
                onShare={handleLinkShare}
              />
            </ViewportOverlay>
          )}
        </>
      )}
    </>
  )
}

const isPanelTransportMode = (
  mode: PathTransportMode | TransportModesPanelMode
): mode is TransportModesPanelMode =>
  Object.values(TransportModesPanelMode).find((key) => key === mode) !== undefined

function toTransportMode<
  T extends PathTransportMode | TransportModesPanelMode,
  R extends Exclude<PathTransportMode | TransportModesPanelMode, T>
>(mode: T): R {
  if (isPanelTransportMode(mode)) {
    switch (mode) {
      case TransportModesPanelMode.Bike:
        return PathTransportMode.bicycle as R
      case TransportModesPanelMode.Car:
        return PathTransportMode.drive as R
      case TransportModesPanelMode.PublicTransport:
        return PathTransportMode.publicTransport as R
      case TransportModesPanelMode.Walk:
        return PathTransportMode.walk as R
      default:
        return PathTransportMode.drive as R
    }
  }

  switch (mode) {
    case PathTransportMode.bicycle:
      return TransportModesPanelMode.Bike as R
    case PathTransportMode.drive:
      return TransportModesPanelMode.Car as R
    case PathTransportMode.publicTransport:
      return TransportModesPanelMode.PublicTransport as R
    case PathTransportMode.walk:
      return TransportModesPanelMode.Walk as R
    default:
      return TransportModesPanelMode.Car as R
  }
}

function getWaypointsWithUserPosition(
  waypoints: Waypoint[],
  coordinates: Coordinates | null,
  myLocationLabel: string
): Waypoint[] {
  return waypoints.map((waypoint) => {
    const hasUserPosition =
      coordinates !== undefined &&
      coordinates !== null &&
      coordinates.lat !== undefined &&
      coordinates.lng !== undefined

    if (waypoint.type === WaypointType.myLocation) {
      if (hasUserPosition) {
        return { ...waypoint, lat: coordinates.lat, lng: coordinates.lng, name: myLocationLabel }
      }

      return { type: WaypointType.empty }
    }

    return waypoint
  })
}
